// src/pages/projectDetailTabs/PartnerSettlementTab.tsx
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { generateId } from "../../utils/id";
import {
  Partner,
  PartnerDisbursement,
  Project,
  SubContract,
  WageEntry,
} from "../../data/mockData";
import { ProjectFinancials } from "../../engine/projectFinancialsEngine";

import { Card } from "../../components/ui/Card";
import { TableShell, CellClamp } from "../../components/ui/TableShell";

type Props = {
  project: Project;
  financials: ProjectFinancials;

  partners: Partner[];
  disbursements: PartnerDisbursement[]; // filtered to this project
  subContracts: (SubContract & { balance?: number; paidToDate?: number })[]; // enriched ok
  wages: WageEntry[]; // wagesForProject

  addPartnerDisbursement: (d: PartnerDisbursement) => void;
  updateProject: (p: Project) => void;
  onTransferFunds: (method: "Cash" | "Bank", amount: number) => void;
};

const money = (n: number) =>
  `$${(Number(n) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

function clampPct01(n: number) {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

/** ✅ Same fallback logic as OverviewTab */
function computeSubcontractBalance(sc: any): number {
  const direct = Number(sc?.balance);
  if (Number.isFinite(direct)) return direct;

  const total =
    Number(sc?.contractAmount) ||
    Number(sc?.total) ||
    Number(sc?.totalCost) ||
    Number(sc?.agreedAmount) ||
    Number(sc?.amount) ||
    0;

  const paid = Number(sc?.paidToDate) || Number(sc?.paid) || 0;

  const b = total - paid;
  return Number.isFinite(b) ? b : 0;
}

/** ✅ Same total-value logic as OverviewTab (for “out of Y”) */
function computeSubcontractTotalValue(sc: any): number {
  return (
    Number(sc?.contractAmount) ||
    Number(sc?.total) ||
    Number(sc?.totalCost) ||
    Number(sc?.agreedAmount) ||
    Number(sc?.amount) ||
    0
  );
}

export default function PartnerSettlementTab({
  project,
  financials,
  partners,
  disbursements,
  subContracts,
  wages,
  addPartnerDisbursement,
  updateProject,
  onTransferFunds,
}: Props) {
  const { t } = useTranslation();
  const totals: any = (financials as any)?.totals ?? {};
  const method: any = totals?.method ?? null;

  // Receipts / costs (defensive)
  const cashIn = Number(method?.cashIn) || 0;
  const bankIn = Number(method?.bankIn) || 0;

  const cashOut = Number(method?.cashOut) || 0;
  const bankOut = Number(method?.bankOut) || 0;

  // Pools (before advances)
  const poolCashBeforeAdv = Number(method?.cashSurplus) || 0;
  const poolBankBeforeAdv = Number(method?.bankSurplus) || 0;

  // Advances (totals)
  const advancesCashTotal = Number(method?.advancesCash) || 0;
  const advancesBankTotal = Number(method?.advancesBank) || 0;

  // Remaining to settle (after advances)
  const poolCashAvailable = Number(method?.availableToSettleCash) || 0;
  const poolBankAvailable = Number(method?.availableToSettleBank) || 0;

  // Remaining to collect (like Overview tab)
  const totalEstimate = Number(totals?.totalEstimate) || 0;
  const totalReceived = Number(totals?.totalReceived) || 0;
  const remainingToCollect = totalEstimate - totalReceived;
  const hasRemainingToCollect = remainingToCollect > 0.0001;

  // Unpaid wages (same logic as Wages tab)
  const unpaidWagesTotal = useMemo(() => {
    return (wages || [])
      .filter((w) => !w.paid)
      .reduce((s, w) => s + (Number((w as any).totalWages) || 0), 0);
  }, [wages]);

  const hasUnpaidWages = unpaidWagesTotal > 0.0001;

  // ✅ Outstanding subs list + totals using OverviewTab logic
  const outstandingSubcontracts = useMemo(() => {
    return (subContracts || [])
      .map((sc: any) => ({
        id: sc.id,
        label: sc.scope || sc.name || sc.title || sc.id,
        balance: computeSubcontractBalance(sc),
      }))
      .filter((x) => x.balance > 0.0001)
      .sort((a, b) => b.balance - a.balance);
  }, [subContracts]);

  const outstandingSubsTotal = useMemo(() => {
    return (subContracts || []).reduce(
      (s, sc: any) => s + computeSubcontractBalance(sc),
      0
    );
  }, [subContracts]);

  const totalSubcontractValue = useMemo(() => {
    return (subContracts || []).reduce(
      (s: number, sc: any) => s + computeSubcontractTotalValue(sc),
      0
    );
  }, [subContracts]);

  const hasOutstandingSubs = outstandingSubsTotal > 0.0001;

  // Pools negative?
  const isPoolsNegative = poolCashBeforeAdv < 0 || poolBankBeforeAdv < 0;

  // Existing settlements?
  const existingSettlements = disbursements.filter(
    (d) => d.kind === "Settlement"
  );
  const isAlreadySettled =
    existingSettlements.length > 0 || (project as any).status === "Settled";

  // Readiness summary (blocked if any action items or negative pool)
  const isBlocked =
    hasOutstandingSubs ||
    hasUnpaidWages ||
    hasRemainingToCollect ||
    isPoolsNegative ||
    isAlreadySettled;

  // Default equal split
  const defaultPct = partners.length ? 100 / partners.length : 0;
  const [splitPct, setSplitPct] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    partners.forEach((p) => (init[p.id] = String(defaultPct)));
    return init;
  });

  // Normalize shares to sum=1
  const parsedSplit = useMemo(() => {
    const raw = partners.map((p) => ({
      id: p.id,
      pct01: clampPct01((Number(splitPct[p.id]) || 0) / 100),
    }));

    const sum = raw.reduce((s, x) => s + x.pct01, 0);
    if (sum <= 0) {
      const eq = partners.length ? 1 / partners.length : 0;
      return partners.map((p) => ({ partnerId: p.id, share: eq }));
    }
    return raw.map((x) => ({ partnerId: x.id, share: x.pct01 / sum }));
  }, [partners, splitPct]);

  // Advances by partner and method
  const advancesCash = disbursements.filter(
    (d) => d.kind === "Advance" && d.method === "Cash"
  );
  const advancesBank = disbursements.filter(
    (d) => d.kind === "Advance" && d.method === "Bank"
  );

  const advByPartner = useMemo(() => {
    const map: Record<string, { cash: number; bank: number }> = {};
    partners.forEach((p) => (map[p.id] = { cash: 0, bank: 0 }));

    advancesCash.forEach((d) => {
      if (!map[d.partnerId]) map[d.partnerId] = { cash: 0, bank: 0 };
      map[d.partnerId].cash += Number(d.amount) || 0;
    });

    advancesBank.forEach((d) => {
      if (!map[d.partnerId]) map[d.partnerId] = { cash: 0, bank: 0 };
      map[d.partnerId].bank += Number(d.amount) || 0;
    });

    return map;
  }, [partners, advancesCash, advancesBank]);

  // Allocation = poolBeforeAdv split, then subtract partner-specific advances
  const computedCash = useMemo(() => {
    const pool = Math.max(poolCashBeforeAdv, 0);
    return partners.map((p) => {
      const share = parsedSplit.find((x) => x.partnerId === p.id)?.share || 0;
      const gross = pool * share;
      const adv = advByPartner[p.id]?.cash || 0;
      const net = gross - adv;

      return {
        partnerId: p.id,
        partnerName: p.name,
        sharePct: share,
        grossShare: gross,
        advancesTaken: adv,
        payout: Math.max(net, 0),
      };
    });
  }, [partners, parsedSplit, poolCashBeforeAdv, advByPartner]);

  const computedBank = useMemo(() => {
    const pool = Math.max(poolBankBeforeAdv, 0);
    return partners.map((p) => {
      const share = parsedSplit.find((x) => x.partnerId === p.id)?.share || 0;
      const gross = pool * share;
      const adv = advByPartner[p.id]?.bank || 0;
      const net = gross - adv;

      return {
        partnerId: p.id,
        partnerName: p.name,
        sharePct: share,
        grossShare: gross,
        advancesTaken: adv,
        payout: Math.max(net, 0),
      };
    });
  }, [partners, parsedSplit, poolBankBeforeAdv, advByPartner]);

  const totalCashPayout = useMemo(
    () => computedCash.reduce((s, x) => s + (Number(x.payout) || 0), 0),
    [computedCash]
  );
  const totalBankPayout = useMemo(
    () => computedBank.reduce((s, x) => s + (Number(x.payout) || 0), 0),
    [computedBank]
  );

  // Settle readiness includes: subs, wages, remaining-to-collect, pools negative, already settled
  const canSettle = useMemo(() => {
    if (!method) return false;
    if (isAlreadySettled) return false;
    if (hasOutstandingSubs) return false;
    if (hasUnpaidWages) return false;
    if (hasRemainingToCollect) return false;
    if (isPoolsNegative) return false;
    return true;
  }, [
    method,
    isAlreadySettled,
    hasOutstandingSubs,
    hasUnpaidWages,
    hasRemainingToCollect,
    isPoolsNegative,
  ]);

  const settleDisabledReason = useMemo(() => {
    if (!method) return t("partnerSettlement.engineNotReady");
    if (isAlreadySettled) return t("partnerSettlement.alreadySettled");
    if (hasOutstandingSubs) return t("partnerSettlement.outstandingSubsExist");
    if (hasUnpaidWages) return t("partnerSettlement.unpaidWagesExist");
    if (hasRemainingToCollect) return t("partnerSettlement.remainingToCollectExists");
    if (isPoolsNegative) return t("partnerSettlement.negativePools");
    return "";
  }, [
    method,
    isAlreadySettled,
    hasOutstandingSubs,
    hasUnpaidWages,
    hasRemainingToCollect,
    isPoolsNegative,
    t,
  ]);

  const doSettleNow = () => {
    if (!canSettle) return;

    const ok = window.confirm(t("partnerSettlement.settleConfirm"));
    if (!ok) return;

    const today = new Date().toISOString().slice(0, 10);

    computedCash.forEach((row) => {
      if (row.payout <= 0) return;
      addPartnerDisbursement({
        id: generateId("PD"),
        projectId: project.id,
        partnerId: row.partnerId,
        amount: Number(row.payout.toFixed(2)),
        method: "Cash",
        date: today,
        kind: "Settlement",
        notes: "Auto-settlement (cash pool)",
      });
    });

    computedBank.forEach((row) => {
      if (row.payout <= 0) return;
      addPartnerDisbursement({
        id: generateId("PD"),
        projectId: project.id,
        partnerId: row.partnerId,
        amount: Number(row.payout.toFixed(2)),
        method: "Bank",
        date: today,
        kind: "Settlement",
        notes: "Auto-settlement (bank pool)",
      });
    });

    updateProject({ ...project, status: "Settled" } as any);
    alert(t("partnerSettlement.settledAlert"));
  };

  const trailSorted = useMemo(() => {
    return disbursements
      .slice()
      .sort((a, b) => ((a.date || "") < (b.date || "") ? 1 : -1));
  }, [disbursements]);

  // Split + settle controls shown in Partner Payout card header right
  const splitHeaderRight = (
    <div className="psHeaderRight">
      <div className="psSplitInline" title={t("partnerSettlement.splitTip")}>
        <span className="psSplitLabel">{t("partnerSettlement.split")}:</span>

        {partners.map((p, idx) => (
          <span key={p.id} className="psSplitChip">
            <span className="psSplitName">
              <CellClamp lines={1} title={p.name}>
                {p.name}
              </CellClamp>
            </span>

            <input
              className="input psSplitInputInline"
              type="number"
              value={splitPct[p.id] ?? ""}
              onChange={(e) =>
                setSplitPct((prev) => ({ ...prev, [p.id]: e.target.value }))
              }
              disabled={isAlreadySettled}
              inputMode="decimal"
            />
            <span className="psPctInline">%</span>

            {idx < partners.length - 1 ? <span className="psPipe">|</span> : null}
          </span>
        ))}
      </div>

      {!canSettle && settleDisabledReason ? (
        <span className="pageNote psSettleNote">{settleDisabledReason}</span>
      ) : null}

      <button
        className="btn btnSm"
        onClick={doSettleNow}
        disabled={!canSettle}
        type="button"
        title={canSettle ? t("partnerSettlement.settleTitle") : settleDisabledReason}
        style={{ opacity: canSettle ? 1 : 0.55, whiteSpace: "nowrap" }}
      >
        {t("partnerSettlement.settleNow")}
      </button>
    </div>
  );

  return (
    <div className="container">
      <style>{`
        .psHeaderRight{
          display:flex;
          align-items:center;
          gap:10px;
          flex-wrap:wrap;
          justify-content:flex-end;
          min-width: 260px;
        }
        .psSplitInline{
          display:flex;
          align-items:center;
          gap:8px;
          flex-wrap:wrap;
          padding: 6px 10px;
          border: 1px solid var(--border);
          border-radius: 999px;
          background: rgba(255,255,255,0.82);
          box-shadow: var(--shadow-sm);
        }
        .psSplitLabel{
          font-weight: 1000;
          color: rgba(15,23,42,0.72);
          font-size: 12px;
        }
        .psSplitChip{
          display:inline-flex;
          align-items:center;
          gap:6px;
          white-space:nowrap;
        }
        .psSplitName{
          font-weight: 950;
          max-width: 120px;
        }
        .psSplitInputInline{
          width: 64px;
          text-align: right;
          padding: 8px 10px;
          border-radius: 999px;
        }
        .psPctInline{
          font-size: 12px;
          font-weight: 900;
          color: var(--muted);
        }
        .psPipe{
          margin: 0 2px 0 6px;
          color: rgba(15,23,42,0.25);
          font-weight: 900;
        }
        .psSettleNote{
          max-width: 360px;
        }

        .psHeroTitle{
          display:flex;
          gap:10px;
          align-items:center;
          flex-wrap:wrap;
        }
        .psHeroTitleText{
          font-size: 18px;
          font-weight: 1000;
          letter-spacing: -0.2px;
        }

        /* blockers box */
        .psBlockers{
          margin-top: 12px;
          padding: 10px 12px;
          border-radius: 12px;
          border: 1px solid rgba(229,83,61,0.18);
          background: rgba(229,83,61,0.05);
        }
        .psBlockersTitle{
          font-weight: 1000;
          margin-bottom: 6px;
        }
        .psBlockersGrid{
          display:grid;
          gap: 8px;
          grid-template-columns: 1fr 1fr;
        }
        @media (max-width: 900px){
          .psBlockersGrid{ grid-template-columns: 1fr; }
        }
        .psBlockerItem{
          display:flex;
          align-items:flex-start;
          gap:8px;
          color: rgba(15,23,42,0.78);
        }
        .psDot{
          width:10px;
          height:10px;
          border-radius: 999px;
          margin-top: 5px;
          background: rgba(229,83,61,0.85);
          flex: 0 0 auto;
        }
        .psSubsList{
          margin-top: 8px;
          padding-left: 18px;
          color: rgba(15,23,42,0.78);
        }
        .psSubsList li{
          margin: 3px 0;
        }

        /* Cash+Bank tables inside one card */
        .psPayoutGrid{
          display:grid;
          gap:14px;
          grid-template-columns: 1fr 1fr;
          align-items:start;
        }
        @media (max-width: 980px){
          .psPayoutGrid{ grid-template-columns: 1fr; }
        }
        .psColTitle{
          display:flex;
          align-items:center;
          gap:10px;
          margin-bottom: 8px;
          flex-wrap:wrap;
        }

        .psHideSm{ }
        @media (max-width: 680px){
          .psHideSm{ display:none !important; }
          .psSplitName{ max-width: 84px; }
        }
      `}</style>

      {/* HERO */}
      <Card
        title={
          <div className="psHeroTitle">
            <span className="psHeroTitleText">
              {canSettle
                ? t("partnerSettlement.readyTitle")
                : t("partnerSettlement.notReadyTitle")}
            </span>

            <span className={`badge ${isBlocked ? "badgeWarn" : "badgeGood"}`}>
              {canSettle ? t("partnerSettlement.ready") : isAlreadySettled ? t("partnerSettlement.settled") : t("partnerSettlement.blocked")}
            </span>
          </div>
        }
        subtitle={t("partnerSettlement.subtitle", { project: project.name })}
      >
        {!canSettle && !isAlreadySettled ? (
          <div className="psBlockers">
            <div className="psBlockersTitle">{t("partnerSettlement.blockersTitle")}</div>

            <div className="psBlockersGrid">
              {hasOutstandingSubs ? (
                <div className="psBlockerItem">
                  <span className="psDot" />
                  <div>
                    {t("partnerSettlement.outstandingSubsLabel")}{" "}
                    <b className="numBad">{money(outstandingSubsTotal)}</b>
                    {totalSubcontractValue > 0 ? (
                      <>
                        {" "}
                        <span style={{ color: "var(--muted)", fontWeight: 900 }}>
                          ({t("partnerSettlement.outOf")} {money(totalSubcontractValue)})
                        </span>
                      </>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {hasUnpaidWages ? (
                <div className="psBlockerItem">
                  <span className="psDot" />
                  <div>
                    {t("partnerSettlement.unpaidWagesLabel")}{" "}
                    <b className="numBad">{money(unpaidWagesTotal)}</b>
                  </div>
                </div>
              ) : null}

              {hasRemainingToCollect ? (
                <div className="psBlockerItem">
                  <span className="psDot" />
                  <div>
                    {t("partnerSettlement.remainingToCollectLabel")}{" "}
                    <b className="numWarn">
                      {money(Math.max(0, remainingToCollect))}
                    </b>
                    {totalEstimate > 0 ? (
                      <>
                        {" "}
                        <span style={{ color: "var(--muted)", fontWeight: 900 }}>
                          ({t("partnerSettlement.outOf")} {money(totalEstimate)})
                        </span>
                      </>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {isPoolsNegative ? (
                <div className="psBlockerItem">
                  <span className="psDot" />
                  <div style={{ display: "grid", gap: 10 }}>
                    {poolCashBeforeAdv < 0 ? (
                      <div>
                        <div>
                          <b className="numBad">
                            {t("partnerSettlement.fundingShortfall", {
                              method: t("common.cash"),
                              amount: money(Math.abs(poolCashBeforeAdv)),
                            })}
                          </b>
                        </div>
                        <div style={{ marginTop: 4 }}>
                          {t("partnerSettlement.fundingShortfallHelp", {
                            method: t("common.cash"),
                            amount: money(Math.abs(poolCashBeforeAdv)),
                          })}
                        </div>
                        {poolBankBeforeAdv >= 0 ? (
                          <div style={{ marginTop: 4, color: "var(--muted)", fontWeight: 900 }}>
                            {t("partnerSettlement.methodAvailable", {
                              method: t("common.bank"),
                              amount: money(poolBankBeforeAdv),
                            })}
                          </div>
                        ) : null}
                        <button
                          className="btn btnSm"
                          type="button"
                          style={{ marginTop: 8 }}
                          onClick={() => onTransferFunds("Cash", Math.abs(poolCashBeforeAdv))}
                        >
                          {t("partnerSettlement.transferFundsButton", {
                            method: t("common.cash"),
                          })}
                        </button>
                      </div>
                    ) : null}

                    {poolBankBeforeAdv < 0 ? (
                      <div>
                        <div>
                          <b className="numBad">
                            {t("partnerSettlement.fundingShortfall", {
                              method: t("common.bank"),
                              amount: money(Math.abs(poolBankBeforeAdv)),
                            })}
                          </b>
                        </div>
                        <div style={{ marginTop: 4 }}>
                          {t("partnerSettlement.fundingShortfallHelp", {
                            method: t("common.bank"),
                            amount: money(Math.abs(poolBankBeforeAdv)),
                          })}
                        </div>
                        {poolCashBeforeAdv >= 0 ? (
                          <div style={{ marginTop: 4, color: "var(--muted)", fontWeight: 900 }}>
                            {t("partnerSettlement.methodAvailable", {
                              method: t("common.cash"),
                              amount: money(poolCashBeforeAdv),
                            })}
                          </div>
                        ) : null}
                        <button
                          className="btn btnSm"
                          type="button"
                          style={{ marginTop: 8 }}
                          onClick={() => onTransferFunds("Bank", Math.abs(poolBankBeforeAdv))}
                        >
                          {t("partnerSettlement.transferFundsButton", {
                            method: t("common.bank"),
                          })}
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>

            {hasOutstandingSubs && outstandingSubcontracts.length > 0 ? (
              <ul className="psSubsList">
                {outstandingSubcontracts.slice(0, 8).map((x) => (
                  <li key={x.id}>
                    <b>{money(x.balance)}</b>{" "}
                    <span style={{ color: "var(--muted)" }}>—</span>{" "}
                    <span title={x.label}>
                      {x.label.length > 80 ? `${x.label.slice(0, 80)}…` : x.label}
                    </span>{" "}
                    <span style={{ color: "var(--muted)" }}>({x.id})</span>
                  </li>
                ))}
                {outstandingSubcontracts.length > 8 ? (
                  <li style={{ color: "var(--muted)", fontStyle: "italic" }}>
                    {t("partnerSettlement.more", { count: outstandingSubcontracts.length - 8 })}
                  </li>
                ) : null}
              </ul>
            ) : null}

            <div className="pageNote" style={{ marginTop: 10 }}>
              {t("partnerSettlement.clearBlockersNote")}
            </div>
          </div>
        ) : null}

        {!canSettle && isAlreadySettled ? (
          <div className="noteOnCard" style={{ marginTop: 12 }}>
            {t("partnerSettlement.alreadySettledNote")}
          </div>
        ) : canSettle ? (
          <div className="noteOnCard" style={{ marginTop: 12 }}>
            {t("partnerSettlement.readyNote")}
          </div>
        ) : null}
      </Card>

      {/* Settled status */}
      {isAlreadySettled ? (
        <div style={{ marginTop: 14 }}>
          <Card
            title={t("partnerSettlement.settlementStatusTitle")}
            subtitle={t("partnerSettlement.settlementStatusSub")}
            right={<span className="badge badgeGood">OK</span>}
          >
            <div className="pageNote">
              {t("partnerSettlement.settlementReadOnly")}
            </div>
          </Card>
        </div>
      ) : null}

      {/* Pool Summary */}
      <div style={{ marginTop: 14 }}>
        <TableShell
          title={t("partnerSettlement.poolSummaryTitle")}
          subtitle={t("partnerSettlement.poolSummarySub")}
        >
          <table>
            <thead>
              <tr>
                <th />
                <th style={{ textAlign: "right" }}>{t("common.cash")}</th>
                <th style={{ textAlign: "right" }}>{t("common.bank")}</th>
                <th style={{ textAlign: "right" }}>{t("common.total")}</th>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td>
                  <strong>{t("partnerSettlement.poolRowReceived")}</strong>
                </td>
                <td style={{ textAlign: "right" }}>{money(cashIn)}</td>
                <td style={{ textAlign: "right" }}>{money(bankIn)}</td>
                <td style={{ textAlign: "right" }}>{money(cashIn + bankIn)}</td>
              </tr>

              <tr>
                <td>
                  <strong>{t("partnerSettlement.poolRowTrueCosts")}</strong>
                </td>
                <td style={{ textAlign: "right" }}>{money(cashOut)}</td>
                <td style={{ textAlign: "right" }}>{money(bankOut)}</td>
                <td style={{ textAlign: "right" }}>{money(cashOut + bankOut)}</td>
              </tr>

              <tr>
                <td>
                  <strong>{t("partnerSettlement.poolRowSurplus")}</strong>
                </td>
                <td style={{ textAlign: "right" }}>
                  <span className={poolCashBeforeAdv < 0 ? "numBad" : "numGood"}>
                    {money(poolCashBeforeAdv)}
                  </span>
                </td>
                <td style={{ textAlign: "right" }}>
                  <span className={poolBankBeforeAdv < 0 ? "numBad" : "numGood"}>
                    {money(poolBankBeforeAdv)}
                  </span>
                </td>
                <td style={{ textAlign: "right", fontWeight: 900 }}>
                  <span
                    className={
                      poolCashBeforeAdv + poolBankBeforeAdv < 0 ? "numBad" : "numGood"
                    }
                  >
                    {money(poolCashBeforeAdv + poolBankBeforeAdv)}
                  </span>
                </td>
              </tr>

              <tr>
                <td>
                  <strong>{t("partnerSettlement.poolRowAdvances")}</strong>
                </td>
                <td style={{ textAlign: "right" }}>{money(advancesCashTotal)}</td>
                <td style={{ textAlign: "right" }}>{money(advancesBankTotal)}</td>
                <td style={{ textAlign: "right" }}>
                  {money(advancesCashTotal + advancesBankTotal)}
                </td>
              </tr>

              <tr>
                <td>
                  <strong>{t("partnerSettlement.poolRowRemaining")}</strong>
                </td>
                <td style={{ textAlign: "right", fontWeight: 900 }}>
                  {money(Math.max(poolCashAvailable, 0))}
                </td>
                <td style={{ textAlign: "right", fontWeight: 900 }}>
                  {money(Math.max(poolBankAvailable, 0))}
                </td>
                <td style={{ textAlign: "right", fontWeight: 900 }}>
                  {money(Math.max(poolCashAvailable, 0) + Math.max(poolBankAvailable, 0))}
                </td>
              </tr>
            </tbody>
          </table>

          <div className="pageNote" style={{ marginTop: 10 }}>
            {t("partnerSettlement.poolCashWageNote")}
          </div>
        </TableShell>
      </div>

      {/* Partner Payout */}
      <div style={{ marginTop: 14 }}>
        <Card
          title={t("partnerSettlement.partnerPayoutTitle")}
          subtitle={t("partnerSettlement.partnerPayoutSub")}
          right={splitHeaderRight}
        >
          <div className="psPayoutGrid">
            {/* Cash */}
            <div>
              <div className="psColTitle">
                <div style={{ fontWeight: 1000 }}>{t("partnerSettlement.cashPoolTitle")}</div>
                <span className="badge">Cash</span>
              </div>

              <div style={{ overflowX: "auto" }}>
                <table>
                  <thead>
                    <tr>
                      <th>{t("partnerSettlement.colPartner")}</th>
                      <th className="psHideSm" style={{ width: 90, textAlign: "right" }}>{t("partnerSettlement.colShare")}</th>
                      <th className="psHideSm" style={{ width: 130, textAlign: "right" }}>{t("partnerSettlement.colGross")}</th>
                      <th style={{ width: 130, textAlign: "right" }}>{t("partnerSettlement.colAdv")}</th>
                      <th style={{ width: 140, textAlign: "right" }}>{t("partnerSettlement.colPayout")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {computedCash.map((r) => (
                      <tr key={r.partnerId}>
                        <td style={{ fontWeight: 900 }}>{r.partnerName}</td>
                        <td className="psHideSm" style={{ textAlign: "right" }}>
                          {(r.sharePct * 100).toFixed(2)}%
                        </td>
                        <td className="psHideSm" style={{ textAlign: "right" }}>
                          {money(r.grossShare)}
                        </td>
                        <td style={{ textAlign: "right" }}>{money(r.advancesTaken)}</td>
                        <td style={{ textAlign: "right", fontWeight: 1000 }}>
                          {money(r.payout)}
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan={4} style={{ fontWeight: 1000, textAlign: "right" }}>{t("common.total")}</td>
                      <td style={{ textAlign: "right", fontWeight: 1000 }}>
                        {money(totalCashPayout)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Bank */}
            <div>
              <div className="psColTitle">
                <div style={{ fontWeight: 1000 }}>{t("partnerSettlement.bankPoolTitle")}</div>
                <span className="badge">Bank</span>
              </div>

              <div style={{ overflowX: "auto" }}>
                <table>
                  <thead>
                    <tr>
                      <th>{t("partnerSettlement.colPartner")}</th>
                      <th className="psHideSm" style={{ width: 90, textAlign: "right" }}>{t("partnerSettlement.colShare")}</th>
                      <th className="psHideSm" style={{ width: 130, textAlign: "right" }}>{t("partnerSettlement.colGross")}</th>
                      <th style={{ width: 130, textAlign: "right" }}>{t("partnerSettlement.colAdv")}</th>
                      <th style={{ width: 140, textAlign: "right" }}>{t("partnerSettlement.colPayout")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {computedBank.map((r) => (
                      <tr key={r.partnerId}>
                        <td style={{ fontWeight: 900 }}>{r.partnerName}</td>
                        <td className="psHideSm" style={{ textAlign: "right" }}>
                          {(r.sharePct * 100).toFixed(2)}%
                        </td>
                        <td className="psHideSm" style={{ textAlign: "right" }}>
                          {money(r.grossShare)}
                        </td>
                        <td style={{ textAlign: "right" }}>{money(r.advancesTaken)}</td>
                        <td style={{ textAlign: "right", fontWeight: 1000 }}>
                          {money(r.payout)}
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan={4} style={{ fontWeight: 1000, textAlign: "right" }}>{t("common.total")}</td>
                      <td style={{ textAlign: "right", fontWeight: 1000 }}>
                        {money(totalBankPayout)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {!isAlreadySettled ? (
            <div className="pageNote" style={{ marginTop: 10 }}>
              {t("partnerSettlement.splitNormalizeTip")}
            </div>
          ) : null}
        </Card>
      </div>

      {/* Disbursement trail */}
      <div style={{ marginTop: 14 }}>
        <TableShell
          title={t("partnerSettlement.disbursementTitle")}
          subtitle={t("partnerSettlement.disbursementSub")}
          right={<span className="badge">{t("common.audit")}</span>}
        >
          {trailSorted.length === 0 ? (
            <div style={{ color: "var(--muted)", fontStyle: "italic", padding: 6 }}>
              {t("partnerSettlement.noDisbursements")}
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th style={{ width: 120 }}>{t("partnerSettlement.colDate")}</th>
                  <th>{t("partnerSettlement.colPartner")}</th>
                  <th style={{ width: 120 }}>{t("partnerSettlement.colKind")}</th>
                  <th style={{ width: 110 }}>{t("partnerSettlement.colMethod")}</th>
                  <th style={{ width: 150, textAlign: "right" }}>{t("partnerSettlement.colAmount")}</th>
                  <th>{t("partnerSettlement.colNotes")}</th>
                </tr>
              </thead>
              <tbody>
                {trailSorted.map((d) => (
                  <tr key={d.id}>
                    <td>{d.date}</td>
                    <td style={{ fontWeight: 900 }}>
                      {partners.find((p) => p.id === d.partnerId)?.name || d.partnerId}
                    </td>
                    <td>
                      {d.kind === "Settlement" ? (
                        <span className="badge badgeGood">{t("partnerSettlement.kindSettlement")}</span>
                      ) : (
                        <span className="badge badgeWarn">{t("partnerSettlement.kindAdvance")}</span>
                      )}
                    </td>
                    <td>
                      <span className="badge">{d.method}</span>
                    </td>
                    <td style={{ textAlign: "right" }}>{money(d.amount)}</td>
                    <td style={{ minWidth: 220 }}>
                      <CellClamp lines={2} title={d.notes || ""}>
                        {d.notes || "—"}
                      </CellClamp>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </TableShell>
      </div>
    </div>
  );
}
